import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deleteSentNotification,
  filterSentNotificationsBySender,
  getNotificationReceivers,
  getNotificationSenderTitle,
  getSentNotifications,
  isBatchSentItem,
  isEventNotification,
  sendNotification,
  updateSentNotification,
} from '../services/notificationService';

const inputClassName =
  'mt-2 w-full rounded-[12px] border border-slate-200 bg-[#F8F8F8] px-4 py-3 text-[13px] outline-none transition focus:border-[#00C0B9] focus:bg-white';

function formatNotificationDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}

function getReceiverLabel(receiver) {
  if (receiver.role === 'admin') {
    return 'مدیر مدرسه';
  }

  const fullName = [receiver.firstName, receiver.lastName].filter(Boolean).join(' ');

  return `معلم: ${fullName || 'معلم'}`;
}

function getReceiverRoleLabel(role) {
  if (role === 'teacher') {
    return 'معلم';
  }

  if (role === 'admin') {
    return 'مدیر';
  }

  return role || 'نامشخص';
}

function MessageCard({ message, tone = 'neutral' }) {
  return (
    <div className="rounded-[8px] bg-[#F8F8F8] px-4 py-5 text-center">
      <p className={`text-[12px] ${tone === 'error' ? 'font-medium text-[#E02727]' : 'text-slate-500'}`}>
        {message}
      </p>
    </div>
  );
}

function NotificationMessagingSection({
  senderRole,
  variant = 'student',
  section = 'all',
  onSentCountChange,
  onSent,
  onOpenConversation,
}) {
  const [receivers, setReceivers] = useState({ teachers: [], admins: [] });
  const [sentNotifications, setSentNotifications] = useState([]);
  const [selectedReceiverId, setSelectedReceiverId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isReceiversLoading, setIsReceiversLoading] = useState(true);
  const [isSentLoading, setIsSentLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [deletingNotificationId, setDeletingNotificationId] = useState(null);
  const [editingNotification, setEditingNotification] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [receiversError, setReceiversError] = useState('');
  const [sentError, setSentError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [editError, setEditError] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const showCompose = section === 'all' || section === 'compose';
  const showSent = section === 'all' || section === 'sent';

  const receiverOptions = useMemo(() => {
    const teachers = (receivers.teachers || []).map((teacher) => ({
      id: teacher.id,
      role: teacher.role,
      label: getReceiverLabel(teacher),
    }));

    const admins = (receivers.admins || []).map((admin) => ({
      id: admin.id,
      role: admin.role,
      label: getReceiverLabel(admin),
    }));

    return [...teachers, ...admins];
  }, [receivers]);

  const filteredSentNotifications = useMemo(
    () => filterSentNotificationsBySender(sentNotifications, senderRole),
    [sentNotifications, senderRole]
  );

  const loadSentNotifications = useCallback(async () => {
    setIsSentLoading(true);
    setSentError('');

    try {
      const items = await getSentNotifications();
      setSentNotifications(items);
    } catch (error) {
      setSentError(error.message || 'امکان دریافت پیام‌های ارسال‌شده وجود ندارد.');
    } finally {
      setIsSentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showCompose) {
      return undefined;
    }

    let isMounted = true;

    async function loadReceivers() {
      setIsReceiversLoading(true);
      setReceiversError('');

      try {
        const data = await getNotificationReceivers();

        if (isMounted) {
          setReceivers(data);
        }
      } catch (error) {
        if (isMounted) {
          setReceiversError(error.message || 'امکان دریافت لیست گیرندگان وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsReceiversLoading(false);
        }
      }
    }

    loadReceivers();

    return () => {
      isMounted = false;
    };
  }, [showCompose]);

  useEffect(() => {
    if (!showSent) {
      return undefined;
    }

    loadSentNotifications();
  }, [loadSentNotifications, showSent]);

  useEffect(() => {
    if (!showSent || !onSentCountChange) {
      return;
    }

    onSentCountChange(filteredSentNotifications.length);
  }, [filteredSentNotifications.length, onSentCountChange, showSent]);

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!selectedReceiverId) {
      setFormError('لطفا گیرنده پیام را انتخاب کنید.');
      return;
    }

    const selectedReceiver = receiverOptions.find((receiver) => receiver.id === selectedReceiverId);

    if (!selectedReceiver) {
      setFormError('گیرنده انتخاب‌شده معتبر نیست.');
      return;
    }

    setIsSending(true);

    try {
      const sentNotification = await sendNotification({
        receiverId: selectedReceiverId,
        receiverRole: selectedReceiver.role,
        senderRole,
        title: title.trim(),
        description: description.trim(),
      });

      setTitle('');
      setDescription('');
      setSelectedReceiverId('');
      setFormSuccess('پیام شما با موفقیت ارسال شد.');
      onSent?.();

      if (showSent) {
        await loadSentNotifications();
      } else if (sentNotification) {
        setSentNotifications((currentNotifications) => [sentNotification, ...currentNotifications]);
      }
    } catch (error) {
      setFormError(error.message || 'امکان ارسال پیام وجود ندارد.');
    } finally {
      setIsSending(false);
    }
  }

  async function handleDeleteNotification(notificationId) {
    const confirmed = window.confirm('آیا از حذف این پیام ارسال‌شده مطمئن هستید؟');

    if (!confirmed) {
      return;
    }

    setDeleteError('');
    setDeletingNotificationId(notificationId);

    try {
      await deleteSentNotification(notificationId);
      setSentNotifications((currentNotifications) =>
        currentNotifications.filter((notification) => notification.id !== notificationId)
      );
    } catch (error) {
      setDeleteError(error.message || 'امکان حذف پیام وجود ندارد.');
    } finally {
      setDeletingNotificationId(null);
    }
  }

  function openEditNotification(notification) {
    setEditingNotification(notification);
    setEditTitle(notification.title || '');
    setEditDescription(notification.description || '');
    setEditError('');
  }

  function closeEditNotification() {
    setEditingNotification(null);
    setEditTitle('');
    setEditDescription('');
    setEditError('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    if (!editingNotification) {
      return;
    }

    const payload = {};
    const nextTitle = editTitle.trim();
    const nextDescription = editDescription.trim();

    if (nextTitle) {
      payload.title = nextTitle;
    }
    if (nextDescription) {
      payload.description = nextDescription;
    }

    if (Object.keys(payload).length === 0) {
      setEditError('حداقل یک فیلد را برای ویرایش وارد کنید.');
      return;
    }

    setIsEditing(true);
    setEditError('');

    try {
      await updateSentNotification(editingNotification.id, payload);
      setSentNotifications((currentNotifications) =>
        currentNotifications.map((notification) =>
          notification.id === editingNotification.id
            ? { ...notification, ...payload }
            : notification
        )
      );
      closeEditNotification();
    } catch (error) {
      setEditError(error.message || 'امکان ویرایش پیام وجود ندارد.');
    } finally {
      setIsEditing(false);
    }
  }

  const isParentVariant = variant === 'parent';
  const isStandaloneSection = section !== 'all';
  const containerClassName = isStandaloneSection
    ? 'space-y-4'
    : isParentVariant
      ? 'space-y-4 rounded-[12px] border border-slate-100 bg-white p-5'
      : 'space-y-4 rounded-[12px] border border-slate-100 bg-white p-4';
  const submitButtonClassName = isParentVariant
    ? 'w-full rounded-[12px] bg-[#1FC24E] py-2.5 text-[13px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60'
    : 'w-full rounded-[8px] bg-[#00C0B9] py-2.5 text-[13px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <div className={containerClassName}>
      {showCompose && (
        <div className={isStandaloneSection ? 'rounded-[12px] border border-slate-100 bg-white p-4' : ''}>
          {section === 'all' && (
            <div>
              <p className="text-[14px] font-medium text-slate-800">ارسال پیام</p>
              <p className="mt-1 text-[12px] text-slate-500">
                {isParentVariant
                  ? 'پیام خود را برای معلم یا مدیر مدرسه ارسال کنید.'
                  : 'برای معلم یا مدیر مدرسه پیام بفرست.'}
              </p>
            </div>
          )}

          {isStandaloneSection && (
            <div className="mb-4">
              <p className="text-[14px] font-medium text-slate-800">ارسال پیام جدید</p>
              <p className="mt-1 text-[12px] text-slate-500">
                {isParentVariant
                  ? 'پیام خود را برای معلم یا مدیر مدرسه ارسال کنید.'
                  : 'برای معلم یا مدیر مدرسه پیام بفرست.'}
              </p>
            </div>
          )}

          {receiversError ? (
            <MessageCard message={receiversError} tone="error" />
          ) : isReceiversLoading ? (
            <MessageCard message="در حال دریافت لیست گیرندگان..." />
          ) : receiverOptions.length === 0 ? (
            <MessageCard message="گیرنده‌ای برای ارسال پیام در دسترس نیست." />
          ) : (
            <form className={section === 'all' ? 'mt-4 space-y-4' : 'space-y-4'} onSubmit={handleSubmit}>
              <label className="block text-[12px] text-slate-700">
                گیرنده
                <select
                  className={inputClassName}
                  value={selectedReceiverId}
                  onChange={(event) => setSelectedReceiverId(event.target.value)}
                  required
                >
                  <option value="">انتخاب گیرنده</option>
                  {receiverOptions.map((receiver) => (
                    <option key={receiver.id} value={receiver.id}>
                      {receiver.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-[12px] text-slate-700">
                عنوان
                <input
                  className={inputClassName}
                  type="text"
                  placeholder="عنوان پیام را وارد کنید"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                />
              </label>

              <label className="block text-[12px] text-slate-700">
                متن پیام
                <textarea
                  className={`${inputClassName} min-h-[110px] resize-y`}
                  placeholder="متن پیام خود را بنویسید"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  required
                />
              </label>

              <button type="submit" className={submitButtonClassName} disabled={isSending}>
                {isSending ? 'در حال ارسال...' : 'ارسال پیام'}
              </button>
            </form>
          )}

          {formError && (
            <p className="mt-3 rounded-[12px] bg-red-50 px-4 py-3 text-[12px] text-red-600">{formError}</p>
          )}

          {formSuccess && (
            <p className="mt-3 rounded-[12px] bg-green-50 px-4 py-3 text-[12px] text-[#1FC24E]">{formSuccess}</p>
          )}
        </div>
      )}

      {showSent && (
        <div
          className={
            isStandaloneSection
              ? 'space-y-3'
              : 'space-y-3 border-t border-slate-100 pt-4'
          }
        >
          {section === 'all' && (
            <div>
              <p className="text-[14px] font-medium text-slate-800">پیام‌های ارسال‌شده</p>
              <p className="mt-1 text-[12px] text-slate-500">
                {filteredSentNotifications.length} پیام ارسال‌شده
              </p>
            </div>
          )}

          {isStandaloneSection && (
            <div className="flex items-center justify-between gap-3 px-0.5">
              <p className="text-[12px] text-slate-500">
                {isSentLoading ? '...' : `${filteredSentNotifications.length} پیام`}
              </p>
            </div>
          )}

          {deleteError && (
            <p className="rounded-[12px] bg-red-50 px-4 py-3 text-[12px] text-red-600">{deleteError}</p>
          )}

          {sentError ? (
            <MessageCard message={sentError} tone="error" />
          ) : isSentLoading ? (
            <MessageCard message="در حال دریافت پیام‌های ارسال‌شده..." />
          ) : filteredSentNotifications.length === 0 ? (
            <div className="rounded-[12px] border border-slate-100 bg-white px-4 py-8 text-center">
              <p className="text-[14px] font-medium text-slate-800">هنوز پیامی ارسال نکرده‌اید</p>
              <p className="mt-2 text-[12px] text-slate-500">
                پیام‌هایی که می‌فرستید اینجا نمایش داده می‌شوند.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {filteredSentNotifications.map((notification) => {
                const isDeleting = deletingNotificationId === notification.id;
                const canEdit = !isEventNotification(notification);

                return (
                  <div
                    key={notification.id}
                    className="rounded-[12px] border border-slate-100 bg-white p-3.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-medium text-slate-800">
                          {notification.title || 'بدون عنوان'}
                        </p>
                        <p className="mt-1 text-[11px] text-slate-400">
                          {getNotificationSenderTitle(notification)}
                          {' · '}
                          {getReceiverRoleLabel(notification.receiverRole)}
                          {' · '}
                          {formatNotificationDate(notification.createdAt)}
                          {notification.parentId ? ' · پاسخ' : ''}
                        </p>
                      </div>

                      <div className="flex shrink-0 items-center gap-1">
                        {onOpenConversation ? (
                          <button
                            type="button"
                            onClick={() => onOpenConversation(notification.id)}
                            className={`rounded-[6px] px-2 py-1 text-[11px] font-medium transition ${
                              isParentVariant
                                ? 'text-[#1FC24E] hover:bg-green-50'
                                : 'text-[#00A8A2] hover:bg-teal-50'
                            }`}
                          >
                            گفتگو
                          </button>
                        ) : null}

                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => openEditNotification(notification)}
                            className="rounded-[6px] px-2 py-1 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50"
                          >
                            ویرایش
                          </button>
                        ) : null}

                        <button
                          type="button"
                          onClick={() => handleDeleteNotification(notification.id)}
                          disabled={Boolean(deletingNotificationId)}
                          className="rounded-[6px] px-2 py-1 text-[11px] font-medium text-[#DC2626] transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isDeleting ? '...' : 'حذف'}
                        </button>
                      </div>
                    </div>

                    <p className="mt-2 line-clamp-3 text-[12px] leading-6 text-slate-600">
                      {notification.description || 'متنی برای این پیام ثبت نشده است.'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {editingNotification ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-[16px] bg-white p-5 shadow-xl">
            <p className="text-[14px] font-medium text-slate-800">ویرایش پیام</p>

            {isBatchSentItem(editingNotification) ? (
              <p className="mt-2 rounded-[10px] bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
                این پیام گروهی است؛ ویرایش روی همه گیرنده‌های همین ارسال اعمال می‌شود.
              </p>
            ) : null}

            <form className="mt-4 space-y-3" onSubmit={handleEditSubmit}>
              <label className="block text-[12px] text-slate-700">
                عنوان
                <input
                  className={inputClassName}
                  type="text"
                  value={editTitle}
                  onChange={(event) => setEditTitle(event.target.value)}
                  required
                />
              </label>

              <label className="block text-[12px] text-slate-700">
                متن پیام
                <textarea
                  className={`${inputClassName} min-h-[110px] resize-y`}
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                  required
                />
              </label>

              {editError ? (
                <p className="rounded-[12px] bg-red-50 px-4 py-3 text-[12px] text-red-600">
                  {editError}
                </p>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeEditNotification}
                  disabled={isEditing}
                  className="rounded-[8px] px-3 py-2 text-[12px] text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={isEditing}
                  className={
                    isParentVariant
                      ? 'rounded-[8px] bg-[#1FC24E] px-3 py-2 text-[12px] font-medium text-white disabled:opacity-60'
                      : 'rounded-[8px] bg-[#00C0B9] px-3 py-2 text-[12px] font-medium text-white disabled:opacity-60'
                  }
                >
                  {isEditing ? 'در حال ذخیره...' : 'ذخیره'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default NotificationMessagingSection;
